import { useState, useEffect } from 'react'

/**
 * Custom hook for fetching data from public/data directory
 * @param {string} url - The URL to fetch from (relative to base URL)
 * @returns {Object} { data, loading, error, refetch }
 */
export const useFetchData = (url) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Construct full URL with base path
      const baseUrl = import.meta.env.BASE_URL || '/'
      const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url.replace(/^\//, '')}`

      const response = await fetch(fullUrl)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      let result

      if (contentType && contentType.includes('application/json')) {
        result = await response.json()
      } else if (url.endsWith('.csv')) {
        const text = await response.text()
        result = parseCSV(text)
      } else {
        result = await response.text()
      }

      setData(result)
    } catch (err) {
      console.error('Error fetching data:', err)
      setError(err.message)
      
      // Try to load from cache if available
      tryLoadFromCache(url)
    } finally {
      setLoading(false)
    }
  }

  const tryLoadFromCache = async (url) => {
    if ('caches' in window) {
      try {
        const cache = await caches.open('data-cache')
        const cachedResponse = await cache.match(url)
        if (cachedResponse) {
          const cachedData = await cachedResponse.json()
          setData(cachedData)
          setError('Using cached data (offline)')
        }
      } catch (cacheErr) {
        console.error('Error loading from cache:', cacheErr)
      }
    }
  }

  useEffect(() => {
    if (url) {
      fetchData()
    }
  }, [url])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}

/**
 * Simple CSV parser
 * @param {string} text - CSV text
 * @returns {Array} Parsed data
 */
const parseCSV = (text) => {
  const lines = text.trim().split('\n')
  const headers = lines[0].split(',').map(h => h.trim())
  
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim())
    const obj = {}
    headers.forEach((header, index) => {
      obj[header] = values[index]
    })
    return obj
  })
}

/**
 * Hook for fetching multiple data files
 * @param {Array<string>} urls - Array of URLs to fetch
 * @returns {Object} { data, loading, error }
 */
export const useFetchMultiple = (urls) => {
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true)
        setError(null)

        const promises = urls.map(async (url) => {
          const baseUrl = import.meta.env.BASE_URL || '/'
          const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url.replace(/^\//, '')}`
          const response = await fetch(fullUrl)
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            return { url, data: await response.json() }
          } else if (url.endsWith('.csv')) {
            const text = await response.text()
            return { url, data: parseCSV(text) }
          } else {
            return { url, data: await response.text() }
          }
        })

        const results = await Promise.all(promises)
        const dataMap = {}
        results.forEach(({ url, data }) => {
          dataMap[url] = data
        })
        
        setData(dataMap)
      } catch (err) {
        console.error('Error fetching multiple data sources:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (urls && urls.length > 0) {
      fetchAll()
    }
  }, [JSON.stringify(urls)])

  return { data, loading, error }
}
